import React, { useEffect, useState } from 'react';
import { selectConsultationsRemaining, selectIsFeaturedSession, useGameStore } from '../../store/gameStore';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { User, RefreshCw, MessageCircle, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { gameAPI } from '../../services/gameAPI';
import { hasBeenInterviewed } from '../../utils/actionHelpers';

export const LeftPanel = () => {
  const {
    guideCharacter,
    consultationsUsed,
    consultationLimit,
    selectedTheme,
    selectedCharacter,
    caseData,
    sessionId,
    interrogationDialogues,
    messageHistory,
    actionProcessing
  } = useGameStore();
  // Featured portraits are authored and shipped on disk; there is no
  // regeneration route for them, so the control is hidden rather than left to
  // fail (`POST /api/game/regenerate-image` has no featured counterpart).
  const isFeatured = useGameStore(selectIsFeaturedSession);

  const [regenerating, setRegenerating] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageEnlarged, setImageEnlarged] = useState(false);
  const { toast } = useToast();

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Determine which character to display (selected character or guide by default)
  const displayCharacter = selectedCharacter || (guideCharacter ? { ...guideCharacter, type: 'guide' } : null);

  // A load failure belongs to one image: clear it (and any open enlarge
  // dialog) when the panel moves to a different image. Clues/documents carry
  // `image`, people carry `portrait`.
  const displayedImageSrc = displayCharacter?.image || displayCharacter?.portrait;
  useEffect(() => {
    setImageError(false);
    setImageEnlarged(false);
  }, [displayedImageSrc]);

  if (!displayCharacter) return null;

  // What is selected. Clue/document selections carry `kind`, because their own
  // `type` is the item's category (forensic, documentary, ...); people
  // (victim/suspect/guide) have no category of their own and use `type`.
  const selectionKind = displayCharacter.kind || displayCharacter.type;

  const consultationsRemaining = selectConsultationsRemaining({ consultationLimit, consultationsUsed });
  
  const handleRegenerateImage = async () => {
    if (!imageError || regenerating || isFeatured) return;
    
    try {
      setRegenerating(true);
      
      const targetId = selectionKind === 'victim' ? 'victim' :
                       selectionKind === 'guide' ? 'guide' :
                       displayCharacter.id;
      
      const data = await gameAPI.regenerateImage(sessionId, 'character', targetId);
      
      if (data.imageUrl) {
        // Force image reload
        setImageError(false);
        toast({
          title: 'Image regenerated',
          description: 'Character portrait has been updated'
        });
        // Trigger re-render by updating character data
        window.location.reload(); // Temporary - ideally update store
      }
      
    } catch (error) {
      console.error('Failed to regenerate image:', error);
      toast({
        title: 'Regeneration failed',
        description: 'Could not regenerate image. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setRegenerating(false);
    }
  };

  // Get character-specific information based on type
  const getCharacterInfo = () => {
    if (selectionKind === 'victim' && caseData?.victim) {
      return {
        name: caseData.victim.name,
        role: `Victim - ${caseData.victim.occupation}`,
        subtitle: `Age ${caseData.victim.age}`,
        description: caseData.victim.background,
        portrait: displayCharacter.portrait || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=600&fit=crop'
      };
    } else if (selectionKind === 'suspect') {
      // Only show alibi if suspect has been interviewed
      const interviewed = hasBeenInterviewed(displayCharacter.id, interrogationDialogues, messageHistory);
      return {
        name: displayCharacter.name,
        role: displayCharacter.occupation,
        subtitle: `Age ${displayCharacter.age}`,
        description: interviewed ? displayCharacter.alibi : 'Interview to learn alibi',
        portrait: displayCharacter.portrait,
        alibiLocked: !interviewed
      };
    } else if (selectionKind === 'guide') {
      return {
        name: displayCharacter.name,
        role: displayCharacter.role,
        subtitle: displayCharacter.expertise || '',
        description: displayCharacter.initial_briefing || displayCharacter.initialBriefing,
        portrait: displayCharacter.portrait
      };
    } else if (selectionKind === 'clue') {
      return {
        name: displayCharacter.name,
        role: 'Evidence',
        subtitle: displayCharacter.type,
        description: displayCharacter.description,
        portrait: displayCharacter.image || 'https://images.unsplash.com/photo-1575936123452-b67c3203c357?w=400&h=600&fit=crop'
      };
    } else if (selectionKind === 'document') {
      return {
        name: displayCharacter.title,
        role: 'Document',
        subtitle: displayCharacter.type,
        description: displayCharacter.content,
        portrait: displayCharacter.image || 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=400&h=600&fit=crop'
      };
    }
    return {
      name: displayCharacter.name || 'Unknown',
      role: displayCharacter.role || displayCharacter.occupation || '',
      subtitle: '',
      description: '',
      portrait: displayCharacter.portrait
    };
  };

  const characterInfo = getCharacterInfo();

  // Clue and document art is landscape (3:2); portraits are 2:3. Show the
  // whole landscape image letterboxed instead of cropping its centre.
  const isLandscapeArt = selectionKind === 'clue' || selectionKind === 'document';
  // Enlarging only makes sense for a real authored/generated image, not the
  // stock fallback shown when the item has none or the image failed to load.
  const realImage = isLandscapeArt ? displayCharacter.image : displayCharacter.portrait;
  const canEnlarge = Boolean(realImage) && !imageError;

  return (
    <div className="p-3 md:p-4 lg:p-6 space-y-3 md:space-y-4 lg:space-y-6">
      {/* Character Portrait Card */}
      <Card className="bg-black/40 backdrop-blur-sm border-white/10 p-3 md:p-4 space-y-3 md:space-y-4">
        <div className={`relative ${isLandscapeArt ? 'aspect-[3/2]' : 'aspect-[2/3]'} max-w-[200px] mx-auto md:max-w-none overflow-hidden rounded-lg bg-gray-800`}>
          <button
            type="button"
            onClick={() => setImageEnlarged(true)}
            disabled={!canEnlarge}
            aria-label={`Enlarge image: ${characterInfo.name}`}
            className="block w-full h-full cursor-zoom-in disabled:cursor-default rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-inset"
          >
            <img
              src={characterInfo.portrait}
              alt={characterInfo.name}
              className={`w-full h-full ${isLandscapeArt && canEnlarge ? 'object-contain' : 'object-cover'}`}
              onError={(e) => {
                setImageError(true);
                e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=600&fit=crop';
              }}
            />
          </button>
          {imageError && !regenerating && !isFeatured && (
            <button
              onClick={handleRegenerateImage}
              className="absolute top-2 right-2 min-h-touch min-w-touch flex items-center justify-center bg-black/60 hover:bg-black/80 rounded-full transition-colors border border-white/20"
              title="Regenerate image"
            >
              <RefreshCw className="w-5 h-5 text-white" />
            </button>
          )}
          {regenerating && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <RefreshCw className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </div>
        <Dialog open={imageEnlarged && canEnlarge} onOpenChange={setImageEnlarged}>
          <DialogContent
            aria-describedby={undefined}
            className="sm:max-w-[90vw] bg-black/95 border-white/10 p-2 sm:p-4"
          >
            <DialogTitle className="sr-only">{characterInfo.name}</DialogTitle>
            <img
              src={realImage}
              alt={characterInfo.name}
              className="max-w-full max-h-[85vh] w-auto object-contain mx-auto"
            />
          </DialogContent>
        </Dialog>
        <div className="space-y-1 md:space-y-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: themeAccent }} />
            <h2 className="text-lg md:text-xl font-bold text-white">{characterInfo.name}</h2>
          </div>
          <p className="text-xs md:text-sm font-semibold" style={{ color: themeAccent }}>
            {characterInfo.role}
          </p>
          {characterInfo.subtitle && (
            <p className="text-xs text-gray-400">{characterInfo.subtitle}</p>
          )}
        </div>
        {characterInfo.description && (
          <div className="pt-2 border-t border-white/10">
            <p className="text-xs md:text-sm text-gray-300 leading-relaxed italic">
              "{characterInfo.description}"
            </p>
          </div>
        )}
      </Card>

      {/* Consult Button - Only show when guide/detective is displayed */}
      {selectionKind === 'guide' && (
        <div className="space-y-2">
          <Button
            className="w-full min-h-touch text-white font-semibold text-sm md:text-base"
            style={{ backgroundColor: themeAccent }}
            onClick={() => {
              // Trigger consult action - this will be handled by ActionBar
              const event = new CustomEvent('triggerConsult');
              window.dispatchEvent(event);
            }}
            disabled={consultationsRemaining <= 0 || actionProcessing}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Consult {guideCharacter?.name?.split(' ').pop() || 'Guide'}
          </Button>
          <p className="text-xs text-gray-400 text-center">
            {consultationsRemaining} / {consultationLimit || 3} consultations remaining
          </p>
        </div>
      )}
    </div>
  );
};